import { defaultKartWeightForVariant } from "./kartBuild";
import type { KartHorn, KartPattern, KartSticker, KartTopper, KartTrail, KartWeightClass } from "./simulation/types";

/**
 * The loadout is deliberately data-only.  This module has no DOM, storage, or
 * server imports so the same normalizer can be used by the browser and the
 * authoritative Node process.
 */

export const KART_BODY_VARIANTS = Object.freeze(["bottle", "can", "mug", "keg", "shot", "wine", "tankard"] as const);
export type KartBodyVariant = (typeof KART_BODY_VARIANTS)[number];
export type KartBody = KartBodyVariant;

export const KART_WEIGHT_CLASSES = Object.freeze(["light", "medium", "heavy"] as const);
export const KART_PATTERNS = Object.freeze(["solid", "stripe", "rally", "split"] as const);
export const KART_TOPPERS = Object.freeze(["none", "cap", "lime", "crown", "laurel"] as const);
export const KART_STICKERS = Object.freeze(["none", "hop", "bolt", "pint", "medal"] as const);
export const KART_TRAILS = Object.freeze(["bubbles", "flame", "foam", "hops", "neon", "stars", "none"] as const);
export const KART_HORNS = Object.freeze(["toot", "lager", "air", "bell"] as const);

export type KartLoadoutPattern = (typeof KART_PATTERNS)[number];
export type KartLoadoutTopper = (typeof KART_TOPPERS)[number];
export type KartLoadoutSticker = (typeof KART_STICKERS)[number];
export type KartLoadoutTrail = (typeof KART_TRAILS)[number];
export type KartLoadoutHorn = (typeof KART_HORNS)[number];
export type { KartHorn, KartPattern, KartSticker, KartTopper, KartTrail, KartWeightClass } from "./simulation/types";

export const KART_VARIANT_MIN = 0;
export const KART_VARIANT_MAX = KART_BODY_VARIANTS.length - 1;
export const KART_LIVERY_MIN = 0;
export const KART_LIVERY_MAX = 5;
export const KART_WHEEL_STYLE_MIN = 0;
export const KART_WHEEL_STYLE_MAX = 4;
export const KART_COLOR_MIN = 0;
export const KART_COLOR_MAX = 0xffffff;
export const KART_NAME_MAX_LENGTH = 20;
export const KART_RACE_NUMBER_MAX_LENGTH = 3;

export const DEFAULT_KART_NAME = "IAN";
export const DEFAULT_KART_COLOR = 0xf3c552;
export const DEFAULT_KART_ACCENT_COLOR = 0xfff3c4;
export const DEFAULT_KART_DRIVER_COLOR = 0x2d7888;
export const DEFAULT_KART_SKID_COLOR = 0x120c08;
export const DEFAULT_KART_VARIANT = 0;
export const DEFAULT_KART_LIVERY = 0;
export const DEFAULT_KART_WHEEL_STYLE = 0;
export const DEFAULT_KART_PATTERN: KartPattern = "solid";
export const DEFAULT_KART_TOPPER: KartTopper = "none";
export const DEFAULT_KART_STICKER: KartSticker = "none";
export const DEFAULT_KART_UNDERGLOW = 0;
export const DEFAULT_KART_HORN: KartHorn = "toot";
export const DEFAULT_KART_TRAIL: KartTrail = "bubbles";
export const DEFAULT_KART_RACE_NUMBER = "";

/**
 * All fields used by the v2 race state, excluding simulation-only identity and
 * movement fields.  `variant` is the stable numeric body slot used by the v2
 * renderer; its legacy string representation is handled by the migration
 * helper below.
 */
export interface KartLoadout {
  name: string;
  color: number;
  accentColor: number;
  variant: number;
  livery: number;
  wheelStyle: number;
  driverColor: number;
  weightClass: KartWeightClass;
  pattern: KartPattern;
  topper: KartTopper;
  sticker: KartSticker;
  underglow: number;
  horn: KartHorn;
  skidColor: number;
  trail: KartTrail;
  raceNumber: string;
}

/** A partial v2 loadout is accepted at every boundary (storage/network/UI). */
export type KartLoadoutInput = Partial<KartLoadout> | Record<string, unknown>;

/**
 * v1's compact cosmetic object as emitted by the old websocket client.  The
 * fields are intentionally unknown at runtime: this is a boundary type, not a
 * claim that untrusted data has already been validated.
 */
export interface V1Cosmetics {
  b?: unknown;
  c?: unknown;
  a?: unknown;
  p?: unknown;
  h?: unknown;
  s?: unknown;
  t?: unknown;
  n?: unknown;
  l?: unknown;
  u?: unknown;
  o?: unknown;
  /** Some saved/debug payloads wrap the compact object in `cos`. */
  cos?: unknown;
}

/** Canonical v2 defaults. Never mutate this object; normalizers return copies. */
export const DEFAULT_KART_LOADOUT: Readonly<KartLoadout> = Object.freeze({
  name: DEFAULT_KART_NAME,
  color: DEFAULT_KART_COLOR,
  accentColor: DEFAULT_KART_ACCENT_COLOR,
  variant: DEFAULT_KART_VARIANT,
  livery: DEFAULT_KART_LIVERY,
  wheelStyle: DEFAULT_KART_WHEEL_STYLE,
  driverColor: DEFAULT_KART_DRIVER_COLOR,
  weightClass: "medium",
  pattern: DEFAULT_KART_PATTERN,
  topper: DEFAULT_KART_TOPPER,
  sticker: DEFAULT_KART_STICKER,
  underglow: DEFAULT_KART_UNDERGLOW,
  horn: DEFAULT_KART_HORN,
  skidColor: DEFAULT_KART_SKID_COLOR,
  trail: DEFAULT_KART_TRAIL,
  raceNumber: DEFAULT_KART_RACE_NUMBER,
});

// Short aliases are useful to callers that already use the generic "loadout"
// vocabulary. Keep the canonical, descriptive constant above as the source of
// truth.
export const DEFAULT_LOADOUT = DEFAULT_KART_LOADOUT;
export const KART_LOADOUT_DEFAULTS = DEFAULT_KART_LOADOUT;

const BODY_TO_VARIANT: Readonly<Record<KartBodyVariant, number>> = Object.freeze({
  bottle: 0,
  can: 1,
  mug: 2,
  keg: 3,
  shot: 4,
  wine: 5,
  tankard: 6,
});

export const KART_BODY_TO_VARIANT = BODY_TO_VARIANT;

const V1_PATTERNS = Object.freeze(["solid", "stripe", "split"] as const);
const V1_TOPPERS = Object.freeze(["none", "cap", "lime", "crown"] as const);
const V1_STICKERS = Object.freeze(["none", "hop", "bolt", "pint"] as const);
const V1_TRAILS = Object.freeze(["bubbles", "flame", "foam", "hops", "neon", "none"] as const);
const V1_UNDERGLOW_HEX = Object.freeze(["#37d5d3", "#ff4bd8", "#f4c542", "#7d5cff"] as const);

type RecordLike = Record<string, unknown>;

function recordLike(value: unknown): RecordLike | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as RecordLike : null;
}

function finiteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function boundedInteger(value: unknown, minimum: number, maximum: number, fallback: number): number {
  if (!finiteNumber(value)) return fallback;
  return Math.max(minimum, Math.min(maximum, Math.floor(value)));
}

function oneOf<T extends string>(value: unknown, values: readonly T[], fallback: T): T {
  return typeof value === "string" && values.includes(value as T) ? value as T : fallback;
}

function sourceValue(source: RecordLike | null, key: string, fallback: unknown): unknown {
  return source && Object.prototype.hasOwnProperty.call(source, key) ? source[key] : fallback;
}

/** Normalize an integer body slot to one of the seven authored v2 variants. */
export function normalizeKartVariant(value: unknown, fallback = DEFAULT_KART_VARIANT): number {
  return boundedInteger(value, KART_VARIANT_MIN, KART_VARIANT_MAX, boundedInteger(fallback, KART_VARIANT_MIN, KART_VARIANT_MAX, DEFAULT_KART_VARIANT));
}

/** Normalize a livery slot to the five current v2 livery slots (0 through 5). */
export function normalizeKartLivery(value: unknown, fallback = DEFAULT_KART_LIVERY): number {
  return boundedInteger(value, KART_LIVERY_MIN, KART_LIVERY_MAX, boundedInteger(fallback, KART_LIVERY_MIN, KART_LIVERY_MAX, DEFAULT_KART_LIVERY));
}

/** Normalize a wheel style slot to one of the five current v2 wheel styles. */
export function normalizeKartWheelStyle(value: unknown, fallback = DEFAULT_KART_WHEEL_STYLE): number {
  return boundedInteger(value, KART_WHEEL_STYLE_MIN, KART_WHEEL_STYLE_MAX, boundedInteger(fallback, KART_WHEEL_STYLE_MIN, KART_WHEEL_STYLE_MAX, DEFAULT_KART_WHEEL_STYLE));
}

/** Normalize a packed RGB value; non-finite/non-number input uses the fallback. */
export function normalizeKartColor(value: unknown, fallback = DEFAULT_KART_COLOR): number {
  return boundedInteger(value, KART_COLOR_MIN, KART_COLOR_MAX, boundedInteger(fallback, KART_COLOR_MIN, KART_COLOR_MAX, DEFAULT_KART_COLOR));
}

/** Normalize a display name for both HTML and server-generated snapshots. */
export function normalizeKartName(value: unknown, fallback = DEFAULT_KART_NAME): string {
  const normalizedFallback = typeof fallback === "string"
    ? fallback.replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, " ").trim().slice(0, KART_NAME_MAX_LENGTH)
    : DEFAULT_KART_NAME;
  const safeFallback = normalizedFallback || DEFAULT_KART_NAME;
  if (typeof value !== "string") return safeFallback;
  const normalized = value.replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, " ").trim().slice(0, KART_NAME_MAX_LENGTH);
  return normalized || safeFallback;
}

/** Normalize an optional 0-3 character alphanumeric racing number. */
export function normalizeKartRaceNumber(value: unknown, fallback = DEFAULT_KART_RACE_NUMBER): string {
  const normalize = (candidate: unknown): string => typeof candidate === "string"
    ? candidate.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, KART_RACE_NUMBER_MAX_LENGTH)
    : "";
  const normalizedFallback = normalize(fallback);
  return typeof value === "string" ? normalize(value) : normalizedFallback;
}

/** Normalize an enum while retaining a typed fallback for callers. */
export function normalizeKartWeightClass(value: unknown, fallback: KartWeightClass = "medium"): KartWeightClass {
  return oneOf(value, KART_WEIGHT_CLASSES, oneOf(fallback, KART_WEIGHT_CLASSES, "medium"));
}

export function normalizeKartPattern(value: unknown, fallback: KartPattern = DEFAULT_KART_PATTERN): KartPattern {
  return oneOf(value, KART_PATTERNS, oneOf(fallback, KART_PATTERNS, DEFAULT_KART_PATTERN));
}

export function normalizeKartTopper(value: unknown, fallback: KartTopper = DEFAULT_KART_TOPPER): KartTopper {
  return oneOf(value, KART_TOPPERS, oneOf(fallback, KART_TOPPERS, DEFAULT_KART_TOPPER));
}

export function normalizeKartSticker(value: unknown, fallback: KartSticker = DEFAULT_KART_STICKER): KartSticker {
  return oneOf(value, KART_STICKERS, oneOf(fallback, KART_STICKERS, DEFAULT_KART_STICKER));
}

export function normalizeKartTrail(value: unknown, fallback: KartTrail = DEFAULT_KART_TRAIL): KartTrail {
  return oneOf(value, KART_TRAILS, oneOf(fallback, KART_TRAILS, DEFAULT_KART_TRAIL));
}

export function normalizeKartHorn(value: unknown, fallback: KartHorn = DEFAULT_KART_HORN): KartHorn {
  return oneOf(value, KART_HORNS, oneOf(fallback, KART_HORNS, DEFAULT_KART_HORN));
}

/**
 * Return a fresh canonical loadout. Every numeric field rejects non-finite
 * values before clamping, colors are packed integer RGB values, strings are
 * bounded, and every enum is checked against the current v2 whitelist.
 */
export function normalizeKartLoadout(value: unknown, fallback?: Partial<KartLoadout>): KartLoadout {
  const source = recordLike(value);
  const fallbackSource = recordLike(fallback);

  const fallbackVariant = normalizeKartVariant(sourceValue(fallbackSource, "variant", DEFAULT_KART_LOADOUT.variant));
  const variant = normalizeKartVariant(sourceValue(source, "variant", fallbackVariant), fallbackVariant);

  // Authored body defaults have a matching weight family. An explicitly
  // supplied fallback weight still wins, which lets storage migration preserve
  // a player's selected build when the body changes independently.
  const variantWeight = defaultKartWeightForVariant(variant);
  const fallbackWeight = fallbackSource && Object.prototype.hasOwnProperty.call(fallbackSource, "weightClass")
    ? normalizeKartWeightClass(fallbackSource.weightClass, variantWeight)
    : variantWeight;

  return {
    name: normalizeKartName(sourceValue(source, "name", fallbackSource?.name), normalizeKartName(sourceValue(fallbackSource, "name", DEFAULT_KART_LOADOUT.name))),
    color: normalizeKartColor(sourceValue(source, "color", fallbackSource?.color), normalizeKartColor(sourceValue(fallbackSource, "color", DEFAULT_KART_LOADOUT.color))),
    accentColor: normalizeKartColor(sourceValue(source, "accentColor", fallbackSource?.accentColor), normalizeKartColor(sourceValue(fallbackSource, "accentColor", DEFAULT_KART_LOADOUT.accentColor))),
    variant,
    livery: normalizeKartLivery(sourceValue(source, "livery", fallbackSource?.livery), normalizeKartLivery(sourceValue(fallbackSource, "livery", DEFAULT_KART_LOADOUT.livery))),
    wheelStyle: normalizeKartWheelStyle(sourceValue(source, "wheelStyle", fallbackSource?.wheelStyle), normalizeKartWheelStyle(sourceValue(fallbackSource, "wheelStyle", DEFAULT_KART_LOADOUT.wheelStyle))),
    driverColor: normalizeKartColor(sourceValue(source, "driverColor", fallbackSource?.driverColor), normalizeKartColor(sourceValue(fallbackSource, "driverColor", DEFAULT_KART_LOADOUT.driverColor))),
    weightClass: normalizeKartWeightClass(sourceValue(source, "weightClass", fallbackSource?.weightClass), fallbackWeight),
    pattern: normalizeKartPattern(sourceValue(source, "pattern", fallbackSource?.pattern), normalizeKartPattern(sourceValue(fallbackSource, "pattern", DEFAULT_KART_LOADOUT.pattern))),
    topper: normalizeKartTopper(sourceValue(source, "topper", fallbackSource?.topper), normalizeKartTopper(sourceValue(fallbackSource, "topper", DEFAULT_KART_LOADOUT.topper))),
    sticker: normalizeKartSticker(sourceValue(source, "sticker", fallbackSource?.sticker), normalizeKartSticker(sourceValue(fallbackSource, "sticker", DEFAULT_KART_LOADOUT.sticker))),
    underglow: normalizeKartColor(sourceValue(source, "underglow", fallbackSource?.underglow), normalizeKartColor(sourceValue(fallbackSource, "underglow", DEFAULT_KART_LOADOUT.underglow))),
    horn: normalizeKartHorn(sourceValue(source, "horn", fallbackSource?.horn), normalizeKartHorn(sourceValue(fallbackSource, "horn", DEFAULT_KART_LOADOUT.horn))),
    skidColor: normalizeKartColor(sourceValue(source, "skidColor", fallbackSource?.skidColor), normalizeKartColor(sourceValue(fallbackSource, "skidColor", DEFAULT_KART_LOADOUT.skidColor))),
    trail: normalizeKartTrail(sourceValue(source, "trail", fallbackSource?.trail), normalizeKartTrail(sourceValue(fallbackSource, "trail", DEFAULT_KART_LOADOUT.trail))),
    raceNumber: normalizeKartRaceNumber(sourceValue(source, "raceNumber", fallbackSource?.raceNumber), normalizeKartRaceNumber(sourceValue(fallbackSource, "raceNumber", DEFAULT_KART_LOADOUT.raceNumber))),
  };
}

/** Convert a legacy v1 body name into the stable v2 numeric body slot. */
export function v1BodyToKartVariant(value: unknown, fallback = DEFAULT_KART_VARIANT): number {
  if (typeof value !== "string") return normalizeKartVariant(fallback);
  const body = value === "mini-keg" ? "keg" : value;
  return Object.prototype.hasOwnProperty.call(BODY_TO_VARIANT, body)
    ? BODY_TO_VARIANT[body as KartBodyVariant]
    : normalizeKartVariant(fallback);
}

/** Convert a strict six-digit `#rrggbb` string to packed RGB, or use fallback. */
export function hexColorToKartColor(value: unknown, fallback = DEFAULT_KART_COLOR): number {
  if (typeof value !== "string" || !/^#[0-9a-f]{6}$/i.test(value)) return normalizeKartColor(fallback);
  return normalizeKartColor(Number.parseInt(value.slice(1), 16), fallback);
}

function v1UnderglowToColor(value: unknown, fallback: number): number {
  if (value === "none") return DEFAULT_KART_UNDERGLOW;
  if (typeof value === "string" && V1_UNDERGLOW_HEX.includes(value.toLowerCase() as (typeof V1_UNDERGLOW_HEX)[number])) {
    return hexColorToKartColor(value, fallback);
  }
  return normalizeKartColor(fallback);
}

/**
 * Migrate v1's compact `cos` payload into the complete v2 schema. Migration is
 * explicit so callers can choose when to read old storage/network records;
 * absent or invalid legacy fields safely retain the supplied v2 base (or the
 * canonical defaults).
 */
export function migrateV1Cosmetics(value: unknown, base?: Partial<KartLoadout>): KartLoadout {
  const outer = recordLike(value);
  const source = recordLike(outer?.cos) ?? outer;
  const mapped: Partial<KartLoadout> = { ...(base ?? {}) };

  if (source) {
    if (Object.prototype.hasOwnProperty.call(source, "b")) mapped.variant = v1BodyToKartVariant(source.b, mapped.variant);
    if (Object.prototype.hasOwnProperty.call(source, "c")) mapped.color = hexColorToKartColor(source.c, normalizeKartColor(mapped.color, DEFAULT_KART_LOADOUT.color));
    if (Object.prototype.hasOwnProperty.call(source, "a")) mapped.accentColor = hexColorToKartColor(source.a, normalizeKartColor(mapped.accentColor, DEFAULT_KART_LOADOUT.accentColor));
    if (Object.prototype.hasOwnProperty.call(source, "p")) mapped.pattern = oneOf(source.p, V1_PATTERNS, normalizeKartPattern(mapped.pattern, DEFAULT_KART_LOADOUT.pattern));
    if (Object.prototype.hasOwnProperty.call(source, "h")) mapped.topper = oneOf(source.h, V1_TOPPERS, normalizeKartTopper(mapped.topper, DEFAULT_KART_LOADOUT.topper));
    if (Object.prototype.hasOwnProperty.call(source, "s")) mapped.skidColor = hexColorToKartColor(source.s, normalizeKartColor(mapped.skidColor, DEFAULT_KART_LOADOUT.skidColor));
    if (Object.prototype.hasOwnProperty.call(source, "t")) mapped.trail = oneOf(source.t, V1_TRAILS, normalizeKartTrail(mapped.trail, DEFAULT_KART_LOADOUT.trail));
    if (Object.prototype.hasOwnProperty.call(source, "n")) mapped.raceNumber = normalizeKartRaceNumber(source.n, normalizeKartRaceNumber(mapped.raceNumber, DEFAULT_KART_LOADOUT.raceNumber));
    if (Object.prototype.hasOwnProperty.call(source, "l")) mapped.sticker = oneOf(source.l, V1_STICKERS, normalizeKartSticker(mapped.sticker, DEFAULT_KART_LOADOUT.sticker));
    if (Object.prototype.hasOwnProperty.call(source, "u")) mapped.underglow = v1UnderglowToColor(source.u, normalizeKartColor(mapped.underglow, DEFAULT_KART_LOADOUT.underglow));
    if (Object.prototype.hasOwnProperty.call(source, "o")) mapped.horn = normalizeKartHorn(source.o, normalizeKartHorn(mapped.horn, DEFAULT_KART_LOADOUT.horn));
  }

  return normalizeKartLoadout(mapped, base);
}

// Descriptive aliases keep the migration discoverable for callers that prefer
// an explicit v2 suffix or a "map" verb.
export const migrateV1CosmeticsToV2 = migrateV1Cosmetics;
export const mapV1CosmeticsToKartLoadout = migrateV1Cosmetics;

/** True only for plain objects; useful before choosing migration vs v2 parsing. */
export function isKartLoadoutRecord(value: unknown): value is Record<string, unknown> {
  return recordLike(value) !== null;
}
